import React from 'react';
import { Button } from '@/designSystem/Button';
import { LineChart, BarChart3, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import {
  calculateHealthScoreMetrics,
} from '../utils/calculators';
import { TimeseriesDataPoint } from '@/features/charts/types';

interface HealthScoreProps {
  metrics: TimeseriesDataPoint[];
  propertyId: number;
  onViewDetailsClick: () => void;
  propertyTitle?: string;
  propertyDatapoints: TimeseriesDataPoint[];
  currencySymbol: string;
}

export const HealthScore: React.FC<HealthScoreProps> = ({
  metrics,
  propertyId,
  onViewDetailsClick,
  propertyTitle = 'Property Analysis',
  propertyDatapoints,
  currencySymbol,
}) => {
  const values = calculateHealthScoreMetrics(propertyId, metrics);

  if (values === null) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex flex-col items-center justify-center text-center">
        <h2 className="text-xl font-bold mb-2">No data found</h2>
        <p className="text-gray-600">Please try another property</p>
      </div>
    );
  }

  const {
    healthScore,
    pricingScore,
    rankScore,
    potentialHealthScore,
  } = values;

  // Score to text mapping like credit scores
  const getScoreText = (score: number) => {
    if (score >= 85) return { text: 'Excellent', color: 'text-green-600' };
    if (score >= 70) return { text: 'Very Good', color: 'text-green-500' };
    if (score >= 50) return { text: 'Good', color: 'text-yellow-500' };
    if (score >= 30) return { text: 'Fair', color: 'text-orange-500' };
    return { text: 'Poor', color: 'text-red-500' };
  };

  const scoreRating = getScoreText(healthScore);

  // Sparkline logic
  const createSparkline = (data: TimeseriesDataPoint[], field: 'rank' | 'price') => {
    if (!data?.length) return null;

    const sortedData = [...data].sort((a, b) =>
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    const values = sortedData.map(d => field === 'rank' ? d.rank.our : d.price.our);
    const latest = values[values.length - 1];
    const first = values.length > 1 ? values[0] : latest;
    const trend = field === 'rank' ? (latest - first) * -1 : (latest - first); // Invert rank trend

    const max = Math.max(...values);
    const min = Math.min(...values);
    const range = max - min || 1;

    const points = values.map((value, index) => {
      const x = (index / (values.length - 1)) * 100;

      // 🔥 FIX: Invert Y-axis for ranks so lower rank numbers appear higher on chart
      const y = field === 'rank'
        ? ((value - min) / range) * 20  // For ranks: lower numbers (better) = higher on chart
        : 20 - ((value - min) / range) * 20;  // For prices: higher numbers = higher on chart

      return `${x},${y}`;
    });

    const getTrendIcon = () => {
      if (trend > 0) return <TrendingUp className="w-3 h-3 text-green-600" />;
      if (trend < 0) return <TrendingDown className="w-3 h-3 text-red-600" />;
      return <Minus className="w-3 h-3 text-gray-500" />;
    };

    return (
      <div className="mt-2 p-2 bg-gray-50 rounded">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-medium text-gray-600">
            {field === 'rank' ? 'Rank Trend' : 'Price Trend'}
          </span>
          {getTrendIcon()}
        </div>
        <svg width="100%" height="20" viewBox="0 0 100 20" className="mb-1">
          <path
            d={`M ${points.join(' L ')}`}
            fill="none"
            stroke={field === 'rank' ? '#2563EB' : '#F59E0B'}
            strokeWidth="2"
          />
        </svg>
        <div className="text-xs text-gray-500">
          {field === 'rank' ?
            `Current: #${latest}` :
            `Current: ${currencySymbol}${Math.round(latest)}`
          }
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 h-full">
      {/* Property Title */}
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900">{propertyTitle}</h2>
      </div>

      <div className="flex flex-col items-center mb-6">
        <GaugeSVG score={healthScore} />
        <div className="text-center">
          <div className={`text-lg font-semibold ${scoreRating.color} mb-1`}>
            {scoreRating.text}
          </div>
          <div className="text-sm text-green-600 font-medium">
            Potential: {potentialHealthScore}/100
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <div className="flex justify-between items-center mb-2">
            <span className="flex items-center gap-2 text-base font-medium">
              <BarChart3 size={18} className="text-purple-500" />
              Ranking
            </span>
            <span className="font-bold text-yellow-600 text-base">{rankScore}/100</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className="bg-yellow-500 h-3 rounded-full"
              style={{ width: `${rankScore}%` }}
            ></div>
          </div>
          {propertyDatapoints && createSparkline(propertyDatapoints, 'rank')}
        </div>

        <div>
          <div className="flex justify-between items-center mb-2">
            <span className="flex items-center gap-2 text-base font-medium">
              <LineChart size={18} className="text-blue-500" />
              Pricing
            </span>
            <span className="font-bold text-green-600 text-base">{pricingScore}/100</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className="bg-green-500 h-3 rounded-full"
              style={{ width: `${pricingScore}%` }}
            ></div>
          </div>
          {propertyDatapoints && createSparkline(propertyDatapoints, 'price')}
        </div>
      </div>

      <div className="mt-6 space-y-2">
        <Button
          className="w-full bg-blue-600 hover:bg-blue-700 text-white"
          onClick={onViewDetailsClick}
        >
          View Detailed Charts
        </Button>
      </div>
    </div>
  );
};

/**
 * Gauge that shows current score - MADE BIGGER
 */
const GaugeSVG: React.FC<{ score: number }> = ({ score }) => {
  // Score thresholds for each segment
  const segments = [
    { id: 'vpoor', min: 0, max: 30, color: '#E53935' },   // Red
    { id: 'poor', min: 30, max: 50, color: '#FF9800' },  // Orange
    { id: 'fair', min: 50, max: 70, color: '#FDD835' },  // Yellow
    { id: 'good', min: 70, max: 85, color: '#7CB342' },  // Light Green
    { id: 'excellent', min: 85, max: 101, color: '#43A047' }, // Green
  ];

  // Helper to check if segment is active for current score
  const isSegmentActive = (seg: typeof segments[number]) =>
    score >= seg.min;

  return (
    <div className="relative flex items-center justify-center">
      <svg viewBox="0 0 312 312" width="180" height="180">

        {/* 5 arcs, each with distinct offset and the same dasharray. */}
        {segments.map((segment, index) => (
          <circle
            key={segment.id}
            id={segment.id}
            cx="156"
            cy="156"
            r="140"
            fill="none"
            stroke={isSegmentActive(segment) ? segment.color : '#ddd'}
            strokeWidth="28"
            strokeDasharray="126.07 753.58"
            strokeDashoffset={549.51 - (index * 133.37)}
            transform="rotate(0 156 156)"
          />
        ))}

        {/* Label & score text - made bigger */}
        <text
          x="50%"
          y="32%"
          dominantBaseline="middle"
          textAnchor="middle"
          className="text-3xl text-gray-800 fill-current font-medium"
        >
          Health Score
        </text>
        <text
          x="50%"
          y="55%"
          dominantBaseline="middle"
          textAnchor="middle"
          className="text-7xl font-bold text-gray-700 fill-current"
          id="scoreValue"
        >
          {score}
        </text>
        <text
          x="50%"
          y="72%"
          dominantBaseline="middle"
          textAnchor="middle"
          className="text-2xl text-gray-800 fill-current"
        >
          out of 100
        </text>
      </svg>
    </div>
  );
};